import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { downloadAuthed, importsApi, locationsApi, taxonomyApi, LocationRecord, ImportOptions } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/catalog/Skeleton";

interface PreviewState {
  rows_total: number;
  valid_count: number;
  invalid_count: number;
  preview_rows: any[];
  errors: { row: number; column: string; error: string }[];
  truncated: boolean;
}

export default function ItemImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [photoJob, setPhotoJob] = useState<{
    total: number; processed: number; imported: number; failures: number; status: string;
  } | null>(null);
  const photoPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [skipInvalid, setSkipInvalid] = useState(true);
  const [dropping, setDropping] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [zipJob, setZipJob] = useState<{
    total: number; processed: number; attached: number; items_updated: number; unmatched: number; status: string;
  } | null>(null);
  const zipPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [zipResult, setZipResult] = useState<{ attached: number; items_updated: number; unmatched_count: number } | null>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoLive, setPhotoLive] = useState<{ done: number; total: number; attached: number; unmatched: string[] } | null>(null);
  const [photoResult, setPhotoResult] = useState<{ attached: number; total: number; unmatched: string[] } | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => {
    if (zipPollRef.current) clearInterval(zipPollRef.current);
  }, []);

  function startZipJobPolling(jobId: string, total: number) {
    setZipJob({ total, processed: 0, attached: 0, items_updated: 0, unmatched: 0, status: "running" });
    if (zipPollRef.current) clearInterval(zipPollRef.current);
    zipPollRef.current = setInterval(async () => {
      try {
        const j = await importsApi.zipPhotoJob(jobId);
        setZipJob({ total: j.total, processed: j.processed, attached: j.attached, items_updated: j.items_updated, unmatched: j.unmatched, status: j.status });
        if (j.status === "done") {
          if (zipPollRef.current) clearInterval(zipPollRef.current);
          setZipResult({ attached: j.attached, items_updated: j.items_updated, unmatched_count: j.unmatched });
          toast.success(`${j.attached} photo${j.attached === 1 ? "" : "s"} attached to ${j.items_updated} item${j.items_updated === 1 ? "" : "s"}.`);
        }
      } catch {
        /* keep polling; transient errors are fine */
      }
    }, 2500);
  }

  async function runZipUpload(f: File | null) {
    if (!f) return;
    setZipBusy(true); setZipResult(null); setZipProgress(0); setZipJob(null);
    try {
      const res = await importsApi.uploadPhotosZip(f, setZipProgress);
      if (res.photo_job_id) {
        startZipJobPolling(res.photo_job_id, res.total || 0);
      }
    } catch (e: any) {
      toast.error(e?.message || "ZIP upload failed.");
    } finally {
      setZipBusy(false);
      if (zipRef.current) zipRef.current.value = "";
    }
  }

  async function runPhotoFilesUpload(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/") || /\.(png|jpe?g|webp|heic|heif|gif)$/i.test(f.name));
    if (files.length === 0) { toast.error("Please choose image files."); return; }
    setPhotoBusy(true); setPhotoResult(null); setPhotoLive({ done: 0, total: files.length, attached: 0, unmatched: [] });
    try {
      const res = await importsApi.uploadPhotoFiles(files, (done, total, attached, unmatched) =>
        setPhotoLive({ done, total, attached, unmatched }));
      setPhotoResult({ attached: res.attached, total: res.total, unmatched: res.unmatched });
      toast.success(`${res.attached} of ${res.total} photo${res.total === 1 ? "" : "s"} attached${res.unmatched.length ? `, ${res.unmatched.length} didn't match` : ""}.`);
    } catch (e: any) {
      toast.error(e?.message || "Photo upload failed.");
    } finally {
      setPhotoBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  }

  const [defaultLocation, setDefaultLocation] = useState("");
  const [defaultBrand, setDefaultBrand] = useState("");
  const [defaultCategory, setDefaultCategory] = useState("");
  const [locationOptions, setLocationOptions] = useState<LocationRecord[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    locationsApi.list().then(setLocationOptions).catch(() => {});
    taxonomyApi.brands().then((r) => setBrandOptions(r.brands)).catch(() => {});
    taxonomyApi.categories().then((r) => setCategoryOptions(r.categories)).catch(() => {});
  }, []);

  function buildOptions(extra: Partial<ImportOptions> = {}): ImportOptions {
    return {
      skip_invalid: skipInvalid,
      default_location: defaultLocation.trim() || undefined,
      default_brand: defaultBrand.trim() || undefined,
      default_category: defaultCategory.trim() || undefined,
      tab_as_brand: true,
      ...extra,
    };
  }

  function pickFile(f: File | null) {
    setPreview(null);
    setFile(f);
  }

  async function runPreview() {
    if (!file) return;
    setLoadingPreview(true);
    try {
      const res = await importsApi.preview(file, buildOptions());
      setPreview(res);
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not read that file.");
    } finally {
      setLoadingPreview(false);
    }
  }

  async function runCommit(dryRun: boolean) {
    if (!file) return;
    setCommitting(true);
    setPhotoJob(null);
    try {
      const res = await importsApi.commit(file, buildOptions({ dry_run: dryRun }));
      if (dryRun) {
        toast.success(`Dry run: ${res.inserted_count} would import, ${res.skipped_count} skipped.`);
      } else {
        toast.success(`Imported ${res.inserted_count} item${res.inserted_count === 1 ? "" : "s"}.`);
        if (res.photo_job_id && res.photos_pending > 0) {
          // Photos download in the background — show live progress here.
          startPhotoPolling(res.photo_job_id, res.photos_pending);
        } else {
          setTimeout(() => navigate("/app/items"), 1200);
        }
      }
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Import failed.");
    } finally {
      setCommitting(false);
    }
  }

  function startPhotoPolling(jobId: string, total: number) {
    setPhotoJob({ total, processed: 0, imported: 0, failures: 0, status: "running" });
    if (photoPollRef.current) clearInterval(photoPollRef.current);
    photoPollRef.current = setInterval(async () => {
      try {
        const j = await importsApi.photoJob(jobId);
        setPhotoJob({ total: j.total, processed: j.processed, imported: j.imported, failures: j.failures, status: j.status });
        if (j.status === "done") {
          if (photoPollRef.current) clearInterval(photoPollRef.current);
          toast.success(`Photos finished: ${j.imported} added${j.failures > 0 ? `, ${j.failures} link${j.failures === 1 ? "" : "s"} couldn't be fetched` : ""}.`);
        }
      } catch {
        /* keep polling; transient errors are fine */
      }
    }, 2500);
  }

  useEffect(() => () => {
    if (photoPollRef.current) clearInterval(photoPollRef.current);
  }, []);

  async function downloadTemplate() {
    try {
      await downloadAuthed("/imports/items/template", "wardrobe-import-template.csv");
    } catch (e: any) {
      toast.error("Could not download template.");
    }
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10 max-w-4xl mx-auto w-full" data-testid="import-page">
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Onboarding</p>
      <h1 className="font-display text-3xl sm:text-4xl">Bulk import items</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-prose">
        {"Upload a CSV or Excel (.xlsx) file — we'll preview every row before anything is written. Up to 5,000 rows per import. Your own column names are recognised automatically (e.g. Item Description, Code, Colour, Box, Photo)."}
      </p>

      <div className="mt-6 flex items-center gap-3">
        <Button variant="outline" onClick={downloadTemplate}
          data-testid="download-template-btn"
          className="rounded-none border-foreground/20 hover:bg-bone h-10">
          <Download className="w-4 h-4 mr-2" /> Download template
        </Button>
      </div>

      <div className="mt-5 border border-border bg-bone/40 p-4 text-sm text-muted-foreground max-w-prose">
        <p className="text-foreground font-medium mb-1">Adding photos during import</p>
        <p>
          {"Fill the "}
          <span className="font-mono text-[12px] text-oxblood">image_url</span>
          {" column with a link to each item's photo and we'll download it and store it as a real image on the piece. Both "}
          <span className="text-foreground">direct image links</span>
          {" (ending in .jpg / .png / .webp) and "}
          <span className="text-foreground">Google Drive share links</span>
          {" work — Drive files just need to be set to "}
          <em>{"\u201CAnyone with the link.\u201D"}</em>
          {" Add several photos to one item by separating links with a "}
          <span className="font-mono text-[12px]">|</span>
          {" (up to 10). If a link can't be fetched, the item still imports and we'll list which photos failed."}
        </p>
        <p className="mt-3">
          {"Uploading an Excel file with a "}
          <span className="text-foreground">tab per brand</span>
          {"? We import every tab and use each tab's name as that tab's brand automatically."}
        </p>
      </div>

      <div className="mt-6 border border-oxblood/40 bg-oxblood/5 p-4" data-testid="photo-files-section">
        <p className="text-foreground text-sm font-medium mb-1">Upload photos directly <span className="text-oxblood">(recommended)</span></p>
        <p className="text-xs text-muted-foreground mb-3">
          Already imported the items? Just pick your photos — even 100+ at once, straight from your phone. Each photo attaches to the matching item by its filename against the <span className="text-foreground">Photo Page</span> number (then SKU / Box / name). (e.g. <span className="font-mono">1.png</span> → the item whose Photo Page is 1.) They upload in small safe batches, so no size limits. Any that don't match are listed.
        </p>
        <input ref={photoRef} type="file" accept="image/*" multiple className="sr-only"
          data-testid="photo-files-input" onChange={(e) => runPhotoFilesUpload(e.target.files)} />
        <Button type="button" disabled={photoBusy}
          onClick={() => photoRef.current?.click()} data-testid="photo-files-btn"
          className="rounded-none bg-oxblood hover:bg-oxblood-deep text-ivory h-10">
          <Upload className="w-4 h-4 mr-2" /> {photoBusy ? "Uploading…" : "Select photos"}
        </Button>
        {photoBusy && photoLive && (
          <div className="mt-3" data-testid="photo-files-progress">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">Uploading photos…</p>
              <span className="text-xs font-medium text-foreground tabular-nums" data-testid="photo-files-count">
                {photoLive.done} of {photoLive.total} uploaded
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full bg-foreground/10">
              <div className="h-full bg-oxblood transition-all duration-200"
                style={{ width: `${photoLive.total ? Math.round((photoLive.done / photoLive.total) * 100) : 0}%` }} />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {photoLive.attached} attached{photoLive.unmatched.length ? ` · ${photoLive.unmatched.length} didn't match yet` : ""}
            </p>
            {photoLive.unmatched.length > 0 && (
              <div className="mt-1 max-h-24 overflow-auto text-[11px] font-mono text-muted-foreground break-all" data-testid="photo-files-live-unmatched">
                {photoLive.unmatched.slice(0, 100).join(", ")}{photoLive.unmatched.length > 100 ? " …" : ""}
              </div>
            )}
          </div>
        )}
        {!photoBusy && photoResult && (
          <div className="mt-3 text-xs text-muted-foreground" data-testid="photo-files-result">
            <p className="text-foreground font-medium">{photoResult.attached} of {photoResult.total} photos attached{photoResult.unmatched.length ? ` · ${photoResult.unmatched.length} didn't match any item` : ""}.</p>
            {photoResult.unmatched.length > 0 && (
              <details className="mt-1" open>
                <summary className="cursor-pointer text-oxblood">Show {photoResult.unmatched.length} unmatched filename{photoResult.unmatched.length === 1 ? "" : "s"}</summary>
                <p className="mt-1 font-mono break-all max-h-40 overflow-auto">{photoResult.unmatched.slice(0, 200).join(", ")}{photoResult.unmatched.length > 200 ? " …" : ""}</p>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 border border-border bg-bone/30 p-4" data-testid="zip-photos-section">
        <p className="text-foreground text-sm font-medium mb-1">Or bulk photos from a ZIP</p>
        <p className="text-xs text-muted-foreground mb-3">
          Already imported the items? Upload a .zip of images and each photo attaches to the matching item automatically — matched by filename against the <span className="text-foreground">Photo Page</span> number stored on each item, then SKU/Code, Box, or name. (e.g. <span className="font-mono">1.png</span> attaches to the item whose Photo Page is 1.) Unmatched files are reported.
        </p>
        <input ref={zipRef} type="file" accept=".zip,application/zip" className="sr-only"
          data-testid="zip-file-input" onChange={(e) => runZipUpload(e.target.files?.[0] ?? null)} />
        <Button type="button" variant="outline" disabled={zipBusy}
          onClick={() => zipRef.current?.click()} data-testid="zip-upload-btn"
          className="rounded-none border-foreground/20 hover:bg-bone h-10">
          <Upload className="w-4 h-4 mr-2" /> {zipBusy ? `Uploading… ${zipProgress}%` : "Upload photo ZIP"}
        </Button>
        {zipBusy && (
          <div className="mt-3 h-1.5 w-full bg-foreground/10" data-testid="zip-progress">
            <div className="h-full bg-foreground transition-all duration-200" style={{ width: `${zipProgress}%` }} />
          </div>
        )}
        {zipJob && zipJob.status !== "done" && (
          <div className="mt-3" data-testid="zip-job-progress">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">Attaching photos in the background…</p>
              <span className="text-xs text-muted-foreground tabular-nums" data-testid="zip-job-count">
                {zipJob.processed}/{zipJob.total}
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full bg-foreground/10">
              <div className="h-full bg-oxblood transition-all duration-300"
                style={{ width: `${zipJob.total ? Math.round((zipJob.processed / zipJob.total) * 100) : 0}%` }} />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">You can leave this page — photos keep attaching.</p>
          </div>
        )}
        {zipResult && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="zip-result">
            {zipResult.attached} photos attached to {zipResult.items_updated} items · {zipResult.unmatched_count} unmatched
          </p>
        )}
      </div>

      {/* Drop zone */}
      <label
        data-testid="import-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDropping(true); }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault(); setDropping(false);
          const f = e.dataTransfer.files?.[0];
          if (f) pickFile(f);
        }}
        className={`mt-8 grid place-items-center text-center border-2 border-dashed p-12 cursor-pointer transition-colors ${
          dropping ? "border-oxblood bg-oxblood/5" : "border-border bg-bone/40 hover:bg-bone"
        }`}>
        <input ref={inputRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only"
          data-testid="import-file-input"
          onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
        <Upload className="w-8 h-8 text-oxblood mb-3" strokeWidth={1.5} />
        <p className="font-display text-xl">{file ? file.name : "Drop your CSV or Excel file here"}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {file
            ? `${(file.size / 1024).toFixed(1)} KB · click to change`
            : "or click to browse — .csv or .xlsx"}
        </p>
      </label>

      {file && (
        <div className="mt-6 border border-border bg-bone/30 p-4" data-testid="import-defaults">
          <p className="text-foreground text-sm font-medium mb-1">Apply to every row (optional)</p>
          <p className="text-xs text-muted-foreground mb-3">
            Fills in any blank cell — e.g. set the Location for a whole-Miami sheet, or a Brand for a single-brand file. Rows that already have a value keep it.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Location</label>
              <Input data-testid="default-location" list="import-location-options" value={defaultLocation} onChange={(e: any) => setDefaultLocation(e.target.value)} placeholder="e.g. Miami" />
              <datalist id="import-location-options">
                {locationOptions.map((l) => <option key={l._id} value={l.name} />)}
              </datalist>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Brand</label>
              <Input data-testid="default-brand" list="import-brand-options" value={defaultBrand} onChange={(e: any) => setDefaultBrand(e.target.value)} placeholder="Optional" />
              <datalist id="import-brand-options">
                {brandOptions.map((b) => <option key={b} value={b} />)}
              </datalist>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Category</label>
              <select data-testid="default-category" value={defaultCategory}
                onChange={(e) => setDefaultCategory(e.target.value)}
                className="h-10 w-full border border-border bg-background px-2 text-sm">
                <option value="">— Use column / leave blank —</option>
                {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="text-[10px] text-muted-foreground">No Category column? Pick one to apply to the whole file.</p>
            </div>
          </div>
        </div>
      )}

      {file && !preview && (
        <div className="mt-6 flex justify-end">
          <Button onClick={runPreview} disabled={loadingPreview}
            data-testid="preview-btn"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
            {loadingPreview ? "Reading…" : "Preview file"}
          </Button>
        </div>
      )}

      {loadingPreview && <Skeleton className="h-32 mt-6" />}

      {preview && (
        <div className="mt-10" data-testid="import-preview">
          <div className="grid grid-cols-3 gap-4">
            <SummaryCard
              icon={<FileText className="w-4 h-4 text-muted-foreground" />}
              label="Rows" value={String(preview.rows_total)} />
            <SummaryCard
              icon={<CheckCircle2 className="w-4 h-4 text-emerald-700" />}
              label="Valid" value={String(preview.valid_count)} />
            <SummaryCard
              icon={<AlertTriangle className="w-4 h-4 text-oxblood" />}
              label="Invalid" value={String(preview.invalid_count)}
              tone={preview.invalid_count > 0 ? "warn" : "ok"} />
          </div>

          <h2 className="font-display text-xl mt-10 mb-3">First rows</h2>
          {preview.preview_rows.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No rows to show.</p>
          ) : (
            <div className="overflow-x-auto border border-border bg-background">
              <table className="w-full text-xs">
                <thead className="bg-bone/50 text-muted-foreground uppercase tracking-wider">
                  <tr>
                    {["Name", "Brand", "Category", "Condition", "Client", "Location"].map((h) => (
                      <th key={h} className="text-left px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview.preview_rows.map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 truncate max-w-[200px]">{r.name}</td>
                      <td className="px-3 py-2 truncate max-w-[160px]">{r.brand || "—"}</td>
                      <td className="px-3 py-2">{r.category}</td>
                      <td className="px-3 py-2">{r.condition}</td>
                      <td className="px-3 py-2 truncate max-w-[160px]">{r.client_name || "—"}</td>
                      <td className="px-3 py-2 truncate max-w-[160px]">{r.current_location_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.errors.length > 0 && (
            <>
              <h2 className="font-display text-xl mt-10 mb-3">
                Errors <span className="text-sm text-muted-foreground">({preview.errors.length}{preview.truncated ? "+ shown" : ""})</span>
              </h2>
              <div className="border border-border bg-background max-h-72 overflow-y-auto"
                data-testid="import-errors">
                <ul className="divide-y divide-border text-sm">
                  {preview.errors.slice(0, 100).map((e, i) => (
                    <li key={i} className="px-3 py-2">
                      <span className="text-muted-foreground tabular-nums mr-2">#{e.row}</span>
                      <span className="font-mono text-[11px] text-oxblood mr-2">{e.column}</span>
                      <span>{e.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          <h2 className="font-display text-xl mt-10 mb-3">Options</h2>
          <div className="space-y-2 text-sm">
            <Toggle id="opt-skip" checked={skipInvalid} onChange={setSkipInvalid}
              label="Skip invalid rows (uncheck to fail the whole import on first error)" />
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-end">
            <Button variant="outline" onClick={() => runCommit(true)} disabled={committing}
              data-testid="dry-run-btn"
              className="rounded-none border-foreground/20 hover:bg-bone h-10">
              {committing ? "Running…" : "Dry run"}
            </Button>
            <Button onClick={() => runCommit(false)}
              disabled={committing || preview.valid_count === 0}
              data-testid="commit-btn"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 disabled:opacity-50">
              {committing ? "Importing…" : `Import ${preview.valid_count} item${preview.valid_count === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {photoJob && (
        <div className="mt-6 border border-border bg-bone/40 p-4" data-testid="photo-job-progress">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium">
              {photoJob.status === "done" ? "Photos finished downloading" : "Downloading photos in the background…"}
            </p>
            <span className="text-xs text-muted-foreground tabular-nums" data-testid="photo-job-count">
              {photoJob.processed}/{photoJob.total}
            </span>
          </div>
          <div className="mt-3 h-1.5 w-full bg-foreground/10">
            <div
              className="h-full bg-oxblood transition-all duration-300"
              style={{ width: `${photoJob.total ? Math.round((photoJob.processed / photoJob.total) * 100) : 0}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {photoJob.imported} added
            {photoJob.failures > 0 ? ` · ${photoJob.failures} couldn't be fetched` : ""}
            {photoJob.status !== "done" ? " — you can leave this page; photos keep attaching." : ""}
          </p>
          <Button
            onClick={() => navigate("/app/items")}
            data-testid="photo-job-view-items"
            className="mt-3 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-9">
            View items
          </Button>
        </div>
      )}
    </main>
  );
}

function SummaryCard({
  icon, label, value, tone = "neutral",
}: { icon: React.ReactNode; label: string; value: string; tone?: "neutral" | "ok" | "warn" }) {
  return (
    <div className="border border-border bg-background p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {icon} {label}
      </div>
      <p className={`font-display text-3xl mt-2 ${tone === "warn" ? "text-oxblood" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function Toggle({ id, checked, onChange, label }: {
  id: string; checked: boolean; onChange: (v: boolean) => void; label: string;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-3 cursor-pointer select-none">
      <input id={id} type="checkbox" checked={checked}
        data-testid={id}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-oxblood" />
      <span>{label}</span>
    </label>
  );
}
